import { useEffect } from "react"
import Select from "react-select"
import { Checkbox, Modal, TextInput } from "@wfp/ui"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { useGetYearsQuery } from "services/gorp"

type Inputs = {
  id: number
  year: number
  month: string
  visibilityId: number
  countryOfficeIso3: string
  comment: string
  totalValue: number
  totalPrevalence: number
  mainValue: number
  mainPrevalence: number
  mainSource: string
  areRefugeesInMain: boolean
  additionalRefugeesValue: number
  additionalRefugeesPrevalence: number
  additionalRefugeesDataSource: string
  areIdpinMain: boolean
  additionalIdpvalue: number
  additionalIdpprevalence: number
  additionalIdpsource: string
  isBaseline: boolean
}

interface Props {
  onSubmit: (workBookData: any) => void
  handleClose: () => void
  actionItem: any
}

export const regions = [
  { value: "RBB", label: "RBB" },
  { value: "RBC", label: "RBC" },
  { value: "RBD", label: "RBD" },
  { value: "RBJ", label: "RBJ" },
  { value: "RBN", label: "RBN" },
  { value: "RBP", label: "RBP" },
]

const monthData = [
  { value: "Jan", label: "Jan" },
  { value: "Feb", label: "Feb" },
  { value: "Mar", label: "Mar" },
  { value: "Apr", label: "Apr" },
  { value: "May", label: "May" },
  { value: "Jun", label: "Jun" },
  { value: "Jul", label: "Jul" },
  { value: "Aug", label: "Aug" },
  { value: "Sep", label: "Sep" },
  { value: "Oct", label: "Oct" },
  { value: "Nov", label: "Nov" },
  { value: "Dec", label: "Dec" },
]

export const visibility = [
  { value: 1, label: "Global" },
  { value: 2, label: "Regional" },
  { value: 3, label: "Country" },
]

export default function ViewData({ onSubmit, handleClose, actionItem }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
  } = useForm<Inputs>()

  const { data: years } = useGetYearsQuery()

  const countryData = [{ value: 0, label: "All years" }]
  years?.map(c => {
    countryData.push({
      value: Number(c.value),
      label: c.name,
    })
  })

  const beforeSubmit: SubmitHandler<Inputs> = async workBookData => {
    console.log("workBookData", workBookData)
    onSubmit({
      id: workBookData.id,
      gorpID: actionItem.gorpID,
      gorpDateYear: workBookData.year,
      gorpDateMonth: workBookData.month,
      gorpVisibilityId: workBookData.visibilityId,
      countryOfficeIso3: workBookData.countryOfficeIso3,
      gorpComment: workBookData.comment,
      gorpTotalValue: workBookData.totalValue,
      gorpTotalPrevalence: workBookData.totalPrevalence,
      gorpMainValue: workBookData.mainValue,
      gorpMainPrevalence: workBookData.mainPrevalence,
      gorpMainSource: workBookData.mainSource,
      areRefugeesInMain: workBookData.areRefugeesInMain,
      gorpAdditionalRefugeesValue: workBookData.additionalRefugeesValue,
      gorpAdditionalRefugeesPrevalence: workBookData.additionalRefugeesPrevalence,
      gorpAdditionalRefugeesDataSource: workBookData.additionalRefugeesDataSource,
      areIdpinMain: workBookData.areIdpinMain,
      gorpAdditionalIdpvalue: workBookData.additionalIdpvalue,
      gorpAdditionalIdpprevalence: workBookData.additionalIdpprevalence,
      gorpAdditionalIdpsource: workBookData.additionalIdpsource,
      gorpIsBaseline: workBookData.isBaseline,
    })
    reset()
  }

  useEffect(() => {
    if (actionItem) {
      setValue("id", actionItem?.id)
      setValue("year", actionItem?.gorpDateYear)
      setValue("month", actionItem?.gorpDateMonth)
      setValue("visibilityId", actionItem?.gorpVisibilityId)
      setValue("countryOfficeIso3", actionItem?.countryOfficeIso3)
      setValue("comment", actionItem?.gorpComment)
      setValue("totalValue", actionItem?.gorpTotalValue)
      setValue("totalPrevalence", actionItem?.gorpTotalPrevalence)
      setValue("mainValue", actionItem?.gorpMainValue)
      setValue("mainPrevalence", actionItem?.gorpMainPrevalence)
      setValue("mainSource", actionItem?.gorpMainSource)
      setValue("areRefugeesInMain", actionItem?.areRefugeesInMain)
      setValue("additionalRefugeesValue", actionItem?.gorpAdditionalRefugeesValue)
      setValue("additionalRefugeesPrevalence", actionItem?.gorpAdditionalRefugeesPrevalence)
      setValue("additionalRefugeesDataSource", actionItem?.gorpAdditionalRefugeesDataSource)
      setValue("areIdpinMain", actionItem?.areIdpinMain)
      setValue("additionalIdpvalue", actionItem?.gorpAdditionalIdpvalue)
      setValue("additionalIdpprevalence", actionItem?.gorpAdditionalIdpprevalence)
      setValue("additionalIdpsource", actionItem?.gorpAdditionalIdpsource)
      setValue("isBaseline", actionItem?.gorpIsBaseline)
    }
  }, [actionItem])

  return (
    <Modal
      open={!!actionItem}
      modalHeading="Edit Data"
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onRequestSubmit={handleSubmit(beforeSubmit)}
      onRequestClose={handleClose}
      wide
    >
      <div className="mx-auto max-w-[1000px]">
        <div className="flex flex-wrap -mx-3 justify-center">
          {/* Column 1 */}
          <div className="w-full lg:w-1/2 px-3">
            {/* Year */}
            <TextInput
              labelText="Year"
              placeholder="Year"
              defaultValue={actionItem?.gorpDateYear}
              invalid={errors.year}
              invalidText={errors.year?.message}
              {...register("year")}
            />
            {/* Month */}
            <div style={{ marginTop: "10px" }} />
            <label htmlFor="abc" className="wfp--label">
              Month
            </label>
            <Controller
              {...{ control }}
              name="month"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={monthData}
                  value={monthData.find(r => r.value === value)}
                  onChange={changeValue => onChange(changeValue?.value as any)}
                />
              )}
            />
            {/* Visibility */}
            <div style={{ marginTop: "10px" }} />
            <label htmlFor="abc" className="wfp--label">
              Visibility
            </label>
            <Controller
              {...{ control }}
              name="visibilityId"
              render={({ field: { onChange, value, ref } }) => (
                <Select
                  className="wfp--react-select-container"
                  classNamePrefix="wfp--react-select"
                  ref={ref}
                  options={visibility}
                  value={visibility.find(r => r.value === value)}
                  onChange={changeValue => onChange(changeValue?.value as any)}
                />
              )}
            />
            {/* Country */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="Country"
              placeholder="Country"
              defaultValue={actionItem?.countryOfficeIso3}
              invalid={errors.countryOfficeIso3}
              invalidText={errors.countryOfficeIso3?.message}
              {...register("countryOfficeIso3")}
            />
            {/* Acute Food Insecurity Total Value */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="Total Value"
              placeholder="Total Value"
              defaultValue={actionItem?.gorpTotalValue ?? ""}
              {...register("totalValue")}
            />
            {/* Acute Food Insecurity Total Prevalence */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="Total Prevalence"
              placeholder="Total Prevalence"
              defaultValue={actionItem?.totalPrevalence ?? ""}
              {...register("totalPrevalence")}
            />
            {/* Acute Food Insecurity Main Value */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="Main Value"
              placeholder="Main Value"
              defaultValue={actionItem?.mainValue ?? ""}
              {...register("mainValue")}
            />
            {/* Acute Food Insecurity Main Prevalence */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="Main Prevalence"
              placeholder="Main Prevalence"
              defaultValue={actionItem?.mainPrevalence ?? ""}
              {...register("mainPrevalence")}
            />
            {/* Acute Food Insecurity Main Source */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="Main Source"
              placeholder="Main Source"
              defaultValue={actionItem?.mainSource ?? ""}
              {...register("mainSource")}
            />
          </div>
          {/* Column 2 */}
          <div className="w-full lg:w-1/2 px-3">
            {/* Are Refugees In Main */}
            <Controller
              {...{ control }}
              name="areRefugeesInMain"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  checked={value}
                  id="gorpAreRefugeesInMainCheckbox"
                  labelText="areRefugeesInMain"
                  name="checkbox-areRefugeesInMain"
                  onChange={changeValue => onChange(changeValue.target.checked)}
                />
              )}
            />
            {/* Additional Refugees Value */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="Refugees Value"
              placeholder="Refugees Value"
              defaultValue={actionItem?.additionalRefugeesValue ?? ""}
              {...register("additionalRefugeesValue")}
            />
            {/* Additional Refugees Prevalence */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="Refugees Prevalence"
              placeholder="Refugees Prevalence"
              defaultValue={actionItem?.additionalRefugeesPrevalence ?? ""}
              {...register("additionalRefugeesPrevalence")}
            />
            {/* Additional Refugees Data Source */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="Refugees Data Source"
              placeholder="Refugees Data Source"
              defaultValue={actionItem?.additionalRefugeesDataSource ?? ""}
              {...register("additionalRefugeesDataSource")}
            />
            {/* Are Internally Displaced In Main */}
            <div style={{ marginTop: "10px" }} />
            <Controller
              {...{ control }}
              name="areIdpinMain"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  checked={value}
                  id="gorpAreInternallyDisplacedInMainCheckbox"
                  labelText="areIdpinMain"
                  name="checkbox-areIdpinMain"
                  onChange={changeValue => onChange(changeValue.target.checked)}
                />
              )}
            />
            {/* Additional Internally Displaced Value */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="IDP Value"
              placeholder="IDP Value"
              defaultValue={actionItem?.additionalIdpvalue ?? ""}
              {...register("additionalIdpvalue")}
            />
            {/* Additional Internally Displaced Prevalence */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="IDP Prevalence"
              placeholder="IDP Prevalence"
              defaultValue={actionItem?.additionalIdpprevalence ?? ""}
              {...register("additionalIdpprevalence")}
            />
            {/* Additional Internally Displaced Source */}
            <div style={{ marginTop: "10px" }} />
            <TextInput
              labelText="IDP Source"
              placeholder="IDP Source"
              defaultValue={actionItem?.additionalIdpsource ?? ""}
              {...register("additionalIdpsource")}
            />
            {/* comment */}
            <div style={{ marginTop: "30px" }} />
            <TextInput
              labelText="Comment"
              placeholder="Comment"
              defaultValue={actionItem?.gorpComment ?? ""}
              {...register("comment")}
            />
            {/* is Baseline */}
            <div style={{ marginTop: "10px" }} />
            <Controller
              {...{ control }}
              name="isBaseline"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  checked={value}
                  id="gorpisBaselineCheckbox"
                  labelText="is Baseline"
                  name="checkbox-isBaseline"
                  onChange={changeValue => onChange(changeValue.target.checked)}
                />
              )}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}
